import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, inject, model, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";

import { of } from "rxjs";
import { catchError, debounceTime, map, switchMap } from "rxjs/operators";
import { SvgIconComponent } from "angular-svg-icon";

import { AuthService } from "@core/services/auth.service";
import { UserPreferencesService } from "@core/services/user-preferences.service";
import { UserHttpService } from "@features/admin/components/users/services/user-http.service";
import { LoginRoutesConstants } from "@features/login/routes/login.routes.constants";
import { PortalRoutesConstants } from "@features/portal/routes/portal.routes.constants";
import { RegisterInvitedDto } from "@login/models/register.model";
import { ButtonWithLoadingComponent } from "@shared/components/button-with-loading/button-with-loading.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ModalService } from "@shared/components/modal/modal.service";

@Component({
  selector: "component-register-modal",
  templateUrl: "./register-modal.component.html",
  imports: [ModalComponent, ButtonWithLoadingComponent, InputComponent, SvgIconComponent, RouterLink, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterModalComponent extends BaseModalComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _userPreferences = inject(UserPreferencesService);
  private readonly _userHttpService = inject(UserHttpService);
  private readonly _modalService = inject(ModalService);

  // Inputs for invite flow
  readonly email = model<string>("");
  readonly organizationId = model<string>("");

  // Form state
  protected readonly usernameControl = signal<FormControl | undefined>(undefined);
  protected readonly password = signal("");
  protected readonly passwordError = signal<string | null>(null);
  protected readonly isLoading = signal(false);

  protected readonly loginRoutes = LoginRoutesConstants;

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    const usernameControl = new FormControl("", {
      validators: [Validators.maxLength(50)]
    });

    // Add dynamic username validation
    usernameControl.valueChanges
      .pipe(
        debounceTime(500),
        switchMap((value) => {
          usernameControl.markAsTouched();
          if (!value || usernameControl.hasError("maxlength")) {
            return of(null);
          }
          return this._userHttpService.checkUsernameExists(value).pipe(
            map((response) => (response.exists ? { usernameExists: { message: response.message } } : null)),
            catchError(() => of(null))
          );
        })
      )
      .subscribe((errors) => {
        // Manually set errors on the control
        if (errors) {
          usernameControl.setErrors(errors);
        } else if (usernameControl.hasError("usernameExists")) {
          // Clear only the usernameExists error
          const currentErrors = { ...usernameControl.errors };
          delete currentErrors["usernameExists"];
          usernameControl.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null);
        }
      });

    this.usernameControl.set(usernameControl);
  }

  protected onPasswordChange(value: string): void {
    this.password.set(value);
    this.passwordError.set(null);
  }

  protected getUsernameErrorMessage(): string | null {
    const control = this.usernameControl();
    if (!control || !control.touched) return null;

    if (control.errors?.["usernameExists"]) {
      return control.errors["usernameExists"].message;
    }
    if (control.errors?.["maxlength"]) {
      const maxLength = control.errors["maxlength"].requiredLength;
      return `Username cannot exceed ${maxLength} characters`;
    }

    return null;
  }

  protected isFormInvalid(): boolean {
    const usernameControl = this.usernameControl();
    const hasUsernameError = usernameControl?.errors && Object.keys(usernameControl.errors).length > 0;
    const hasPasswordError = !!this.passwordError();

    return hasUsernameError || hasPasswordError || !this.password();
  }

  protected onRegister(): void {
    const usernameControl = this.usernameControl();
    if (!usernameControl) return;

    // Validate form
    let hasError = false;

    if (!this.password() || this.password().length < 8) {
      this.passwordError.set("Password must be at least 8 characters");
      hasError = true;
    } else {
      this.passwordError.set(null);
    }

    if (hasError) {
      return;
    }

    this.isLoading.set(true);

    const registerData: RegisterInvitedDto = {
      email: this.email(),
      password: this.password(),
      organizationId: this.organizationId()
    };

    // Add username if provided
    if (usernameControl.value) {
      registerData.username = usernameControl.value;
    }

    this._authService.registerInvitedUser(registerData).subscribe({
      next: () => {
        this._userPreferences.setIsNewUser(true);
        this.activeModal.close(true);

        // Show welcome modal based on registration type
        const isInviteRegistration = !!(this.email() && this.organizationId());
        this._modalService.openWelcomeModal(isInviteRegistration);

        this._router.navigate([PortalRoutesConstants.main()]);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        const code = (err.error as any)?.code;
        if (code === "USERNAME_ALREADY_EXISTS") {
          usernameControl.setErrors({ usernameExists: { message: "This username is already taken" } });
        } else {
          this.passwordError.set((err.error as any)?.message || "Registration failed");
        }
      }
    });
  }
}
